import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./BookListPages.css";
import "./NotificationsPage.css";

const IMG_BASE = (process.env.REACT_APP_API_URL || "").replace("/api", "");
const POLL_INTERVAL_MS = 8000;

function parseDate(dateString) {
  if (!dateString) return null;
  const normalized = dateString.includes("T")
    ? dateString
    : `${dateString.replace(" ", "T")}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatRelativeTime(dateString) {
  const parsed = parseDate(dateString);
  if (!parsed) return "Just now";

  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks}w ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;

  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears}y ago`;
}

function formatActorName(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "Reader";
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

function getNotificationHeadline(item) {
  const actor = formatActorName(item.actor_display_name);
  const chapterTitle = item.notification_chapter_title || item.comment_chapter_title || "";
  if (item.type === "comment_like") {
    return (
      <>
        <span className="notifications-feed__headline-text">{actor}</span>
        {" liked your comment on "}
        {chapterTitle ? (
          <>
            <span className="notifications-feed__headline-chapter">{chapterTitle}</span>
            {" of "}
          </>
        ) : null}
        <span className="notifications-feed__headline-book">{item.epub_title}</span>
        {":"}
      </>
    );
  }

  return (
    <>
      <span className="notifications-feed__headline-text">{actor}</span>
      {" replied to your comment on "}
      {chapterTitle ? (
        <>
          <span className="notifications-feed__headline-chapter">{chapterTitle}</span>
          {" of "}
        </>
      ) : null}
      <span className="notifications-feed__headline-book">{item.epub_title}</span>
      {":"}
    </>
  );
}

function getNotificationSnippet(item) {
  return item.reply_text || item.comment_text || item.comment_excerpt || "";
}

function NotificationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const shouldMarkRead = location.pathname === "/notifications";

  const fetchNotifications = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;

    if (!apiUrl || !userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/get-notifications.php?uid=${userId}&mark_read=${shouldMarkRead ? 1 : 0}`, {
        credentials: "include",
        cache: "no-store"
      });
      const data = await response.json();
      if (data?.success) {
        const nextNotifications = data.notifications || [];
        setNotifications(nextNotifications);
        window.dispatchEvent(
          new CustomEvent("notifications-updated", {
            detail: {
              source: "notifications-page",
              hasUnread: shouldMarkRead
                ? false
                : nextNotifications.some((item) => !item.is_read)
            }
          })
        );
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, shouldMarkRead]);

  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    };

    const handleNotificationsUpdated = (event) => {
      if (event?.detail?.source === "notifications-page") return;
      fetchNotifications();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("notifications-updated", handleNotificationsUpdated);
    window.addEventListener("focus", fetchNotifications);
    const interval = window.setInterval(fetchNotifications, POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("notifications-updated", handleNotificationsUpdated);
      window.removeEventListener("focus", fetchNotifications);
      window.clearInterval(interval);
    };
  }, [fetchNotifications]);

  return (
    <div className="book-list-page notifications-page app-shell-page">
      <div className="container-fluid px-0 pt-4 pb-5">
        <div className="book-list-page__titlebar notifications-page__titlebar px-3 px-md-4">
          <div className="book-list-page__header">
            <h3 className="mb-0">
              Notifications{" "}
              <span className="book-list-page__count-accent fw-normal">{`\u2022 ${notifications.length}`}</span>
            </h3>
          </div>
        </div>

        {loading ? (
          <p className="book-list-page__empty px-3 px-md-4 mt-4">Loading notifications...</p>
        ) : notifications.length === 0 ? (
          <p className="book-list-page__empty px-3 px-md-4 mt-4">
            Likes on your comments and replies to your threads will show up here.
          </p>
        ) : (
          <div className="bookmarks-feed notifications-feed">
            {notifications.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`bookmarks-feed__item notifications-feed__item text-start${item.is_read ? "" : " notifications-feed__item--unread"}`}
                onClick={() =>
                  navigate(`/epub/${item.epub_id}/read`, {
                    state: {
                      notificationCommentTarget: {
                        commentId: item.comment_id || null,
                        replyId: item.reply_id || null,
                        cfi: item.comment_cfi || ""
                      }
                    }
                  })
                }
              >
                <div className="d-flex align-items-start gap-3 px-3 px-md-4">
                  {item.type === "comment_like" ? (
                    <div className="bookmarks-feed__avatar notifications-feed__avatar notifications-feed__avatar--like">
                      <span className="material-symbols-rounded">favorite</span>
                    </div>
                  ) : (
                    <div className="bookmarks-feed__avatar notifications-feed__avatar">
                      {item.actor_profile_picture ? (
                        <img src={`${IMG_BASE}/${item.actor_profile_picture}`} alt="" />
                      ) : (
                        <span>{formatActorName(item.actor_display_name).charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                  )}

                  <div className="bookmarks-feed__body">
                    <p className="bookmarks-feed__date mb-2">{formatRelativeTime(item.created_at)}</p>
                    <p className="bookmarks-feed__headline mb-3">{getNotificationHeadline(item)}</p>

                    <div
                      className={`notifications-feed__snippet${
                        item.type === "comment_reply"
                          ? " bookmarks-feed__snippet notifications-feed__snippet--reply"
                          : " notifications-feed__snippet--like"
                      }`}
                    >
                      {getNotificationSnippet(item)}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
